// The hero timeline under the headline: two rows on one 5 minute scale. Uptrack
// checks every 30 s; a 5 minute plan checks twice. In the example an outage starts at
// 0:40. The Uptrack person gets the alert at 1:00, fixes the site by 4:00 and goes
// back to vacation. The other person stays on vacation until the 5:00 alert and then
// sits at a red computer, while customers still cannot reach the site. The times are
// an example; the note under the picture says so. Reduced motion shows the last frame.
//
// Time moves through one CSS variable (`--t`, percent of the axis) set every frame, so
// the cursor, the people and the lines move without React. React renders only when a
// check runs, a chip or a word appears, or an icon changes.
'use client'

import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Bell, Check, Laptop, PersonStanding, TreePalm } from 'lucide-react'
import {
  ALERT_S,
  FINAL,
  FIXED_S,
  OTHERS_CHECK_COUNT,
  OTHERS_EVERY_S,
  OUTAGE_S,
  SCALE_S,
  UPTRACK_EVERY_S,
  US_CHECK_COUNT,
  formatSpan,
  frameAt,
  othersCheckDown,
  pct,
  usCheckDown,
  type Frame,
} from './outage-timeline-math'

export type TimelineClock = {
  now: () => number
  request: (cb: (now: number) => void) => number
  cancel: (id: number) => void
}

const browserClock: TimelineClock = {
  now: () => performance.now(),
  request: (cb) => requestAnimationFrame(cb),
  cancel: (id) => cancelAnimationFrame(id),
}

const LABEL =
  'Example: an outage starts at 0:40. With Uptrack, the alert comes at 1:00 and the site is fixed at 4:00. With a 5 minute check, the alert comes at 5:00, and customers still cannot reach the site.'

type Accessory = 'vacation' | 'fixing' | 'vacation-done' | 'urgent'

// What React renders; the continuous part of a frame is `--t`.
type Phase = {
  usChecks: number
  othersChecks: number
  know: boolean
  fixed: boolean
  othersKnow: boolean
  moving: boolean
  fading: boolean
  fixingWord: boolean
  siteBackWord: boolean
  unawareWords: boolean
}

const phaseOf = (f: Frame): Phase => ({
  usChecks: f.usChecks,
  othersChecks: f.othersChecks,
  know: f.know,
  fixed: f.fixed,
  othersKnow: f.othersKnow,
  moving: f.moving,
  fading: f.fading,
  // A word shows once its line is long enough to sit under.
  fixingWord: f.t >= ALERT_S + 30,
  siteBackWord: f.t >= FIXED_S + 24,
  unawareWords: f.t >= OUTAGE_S + 84,
})

const samePhase = (a: Phase, b: Phase) => (Object.keys(a) as (keyof Phase)[]).every((k) => a[k] === b[k])

const ACCESSORY_COLOR: Record<Accessory, string> = {
  vacation: 'text-muted-foreground',
  fixing: 'text-primary',
  'vacation-done': 'text-muted-foreground',
  urgent: 'text-status-down',
}

const xy = (x: string): CSSProperties => ({ ['--x' as string]: x })

export function OutageTimeline({
  onStruckChange,
  clock = browserClock,
}: {
  /** True while the headline word "minutes" should be crossed out: from the fix until the loop fades. */
  onStruckChange?: (struck: boolean) => void
  clock?: TimelineClock
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const onStruck = useRef(onStruckChange)
  useEffect(() => {
    onStruck.current = onStruckChange
  })
  const [phase, setPhase] = useState<Phase>(() => phaseOf(frameAt(0)))
  const [active, setActive] = useState(false)
  const [still, setStill] = useState(false)

  const drawAt = (t: number) => trackRef.current?.style.setProperty('--t', String(pct(t)))

  // Run only while motion is allowed, the tab is visible and the timeline is on screen.
  // Reduced motion shows the last frame, still.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const motion = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null
    let inView = true
    const sync = () => {
      const reduce = !!motion?.matches
      setStill(reduce)
      if (reduce) {
        drawAt(FINAL.t)
        setPhase(phaseOf(FINAL))
      }
      setActive(!reduce && document.visibilityState !== 'hidden' && inView)
    }
    const observer =
      typeof IntersectionObserver === 'function'
        ? new IntersectionObserver((entries) => {
            inView = entries[entries.length - 1].isIntersecting
            sync()
          })
        : null
    observer?.observe(el)
    motion?.addEventListener('change', sync)
    document.addEventListener('visibilitychange', sync)
    sync()
    return () => {
      observer?.disconnect()
      motion?.removeEventListener('change', sync)
      document.removeEventListener('visibilitychange', sync)
    }
  }, [])

  // One frame loop; React state changes only when the phase changes.
  useEffect(() => {
    if (!active) return
    const t0 = clock.now()
    let last: Phase | null = null
    let id = 0
    const tick = (now: number) => {
      const f = frameAt(now - t0)
      drawAt(f.t)
      const p = phaseOf(f)
      if (!last || !samePhase(last, p)) {
        last = p
        setPhase(p)
      }
      id = clock.request(tick)
    }
    id = clock.request(tick)
    return () => clock.cancel(id)
  }, [active, clock])

  const struck = phase.fixed && !phase.fading
  useEffect(() => {
    onStruck.current?.(struck)
  }, [struck])

  const usAccessory: Accessory = !phase.know ? 'vacation' : !phase.fixed ? 'fixing' : 'vacation-done'
  const othersAccessory: Accessory = phase.othersKnow ? 'urgent' : 'vacation'
  const walking = phase.moving && !still

  return (
    <div className="w-full">
      <div
        ref={rootRef}
        data-outage-timeline=""
        role="img"
        aria-label={LABEL}
        className={`relative h-[286px] w-full transition-opacity duration-500 [--label-w:76px] [--reserve:40px] sm:[--label-w:104px] sm:[--reserve:52px] ${
          phase.fading ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div aria-hidden="true">
          <RowLabel row="us" name="Uptrack" first="checks again" second="every" value="30 s" count={phase.usChecks} className="top-[80px]" />
          <RowLabel row="others" name="Others" first="next check" second="in" value="5 min" count={phase.othersChecks} className="top-[188px]" />

          <div ref={trackRef} className="absolute inset-y-0 left-[var(--label-w)] right-[var(--reserve)]" style={{ ['--t' as string]: '0' }}>
            <span className="absolute top-0 left-[var(--x)] whitespace-nowrap text-xs font-semibold text-status-down" style={xy(`calc(${pct(OUTAGE_S)}% + 6px)`)}>
              ✕ outage starts 0:40
            </span>
            <span className="absolute top-5 h-[232px] left-[var(--x)] border-l border-dashed border-status-down" style={xy(`${pct(OUTAGE_S)}%`)} />
            <span
              className={`absolute top-6 h-[228px] w-px bg-primary left-[calc(var(--t)*1%)] transition-opacity ${phase.moving ? 'opacity-40' : 'opacity-0'}`}
            />

            {/* the two rows */}
            <span className="absolute inset-x-0 top-[94px] h-px bg-border" />
            <span className="absolute inset-x-0 top-[202px] h-px bg-border" />
            <Line top={93} kind="unaware" from={pct(OUTAGE_S)} to={pct(ALERT_S)} />
            <Line top={93} kind="fixing" from={pct(ALERT_S)} to={pct(FIXED_S)} />
            <Line top={93} kind="fixed" from={pct(FIXED_S)} to={100} />
            <Line top={201} kind="unaware" from={pct(OUTAGE_S)} to={100} />
            {Array.from({ length: US_CHECK_COUNT }, (_, i) => (
              <Tick key={i} row="us" top="top-[88.5px]" at={pct(i * UPTRACK_EVERY_S)} fired={i < phase.usChecks} down={usCheckDown(i)} />
            ))}
            {Array.from({ length: OTHERS_CHECK_COUNT }, (_, i) => (
              <Tick key={i} row="others" top="top-[196.5px]" at={pct(i * OTHERS_EVERY_S)} fired={i < phase.othersChecks} down={othersCheckDown(i)} />
            ))}

            <Person who="us" accessory={usAccessory} aware={phase.know} walking={walking} className="top-[58px] sm:top-[52px]" />
            <Person who="others" accessory={othersAccessory} aware={false} walking={walking} className="top-[166px] sm:top-[160px]" />

            <Chip id="alert-us" on={phase.know} className="top-5 left-[var(--x)]" style={xy(`calc(${pct(ALERT_S)}% - 14px)`)}>
              <Bell className="h-3.5 w-3.5 text-status-down" strokeWidth={2.2} />
              alert 1:00
            </Chip>
            <Chip id="fixed" ok on={phase.fixed} className="top-5 left-[var(--x)] max-sm:left-auto max-sm:right-0" style={xy(`calc(${pct(FIXED_S)}% - 14px)`)}>
              <Check className="h-3.5 w-3.5" strokeWidth={2.4} />
              fixed 4:00
            </Chip>
            <Chip id="alert-others" on={phase.othersKnow} className="top-[128px] right-0">
              <Bell className="h-3.5 w-3.5 text-status-down" strokeWidth={2.2} />
              alert 5:00
            </Chip>

            <Words id="unaware-us" on={phase.know} className="top-[104px] -translate-x-1/2 text-muted-foreground" x={`${(pct(OUTAGE_S) + pct(ALERT_S)) / 2}%`}>
              {formatSpan(ALERT_S - OUTAGE_S)}
            </Words>
            <Words id="fixing" on={phase.fixingWord} className="top-[104px] text-primary" x={`calc(${pct(ALERT_S)}% + 8px)`}>
              fixing
            </Words>
            <Words id="site-back" on={phase.siteBackWord} className="top-[104px] text-status-up" x={`calc(${pct(FIXED_S)}% + 8px)`}>
              site is back
            </Words>
            <Words id="unaware" on={phase.unawareWords} className="top-[212px] text-muted-foreground" x={`calc(${pct(OUTAGE_S)}% + 8px)`}>
              {phase.othersKnow ? `${formatSpan(SCALE_S - OUTAGE_S)} you did not know` : 'you do not know yet'}
            </Words>
            <Words id="customers" on={phase.unawareWords} className="top-[228px] text-status-down" x={`calc(${pct(OUTAGE_S)}% + 8px)`}>
              {phase.othersKnow ? 'customers still cannot reach ' : 'customers cannot reach '}
              <span className="hidden sm:inline">your site</span>
              <span className="sm:hidden">it</span>
            </Words>

            {/* the time axis */}
            <span className="absolute inset-x-0 top-[252px] h-px bg-border">
              {[0, 1, 2, 3, 4, 5].map((m) => (
                <Fragment key={m}>
                  <span className="absolute -top-1 h-[9px] w-px bg-muted-foreground left-[var(--x)]" style={xy(`${m * 20}%`)} />
                  <span className="num absolute top-[11px] -translate-x-1/2 whitespace-nowrap text-[13px] font-semibold text-body left-[var(--x)]" style={xy(`${m * 20}%`)}>
                    {m}:00
                  </span>
                </Fragment>
              ))}
            </span>
          </div>
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Example times. Many free plans check every 5 minutes.</p>
    </div>
  )
}

function RowLabel({
  row,
  name,
  first,
  second,
  value,
  count,
  className,
}: {
  row: 'us' | 'others'
  name: string
  first: string
  second: string
  value: string
  count: number
  className: string
}) {
  const us = row === 'us'
  return (
    <div data-row={row} className={`absolute left-0 w-[var(--label-w)] pr-2 text-xs leading-[1.4] sm:text-[13px] ${us ? 'text-body' : 'text-muted-foreground'} ${className}`}>
      <span className={`mb-0.5 flex items-center gap-1.5 text-sm font-semibold ${us ? 'text-foreground' : 'text-muted-foreground'}`}>
        <span className={`h-2 w-2 rounded-[2px] ${us ? 'bg-primary' : 'bg-muted-foreground'}`} />
        {name}
      </span>
      <span className="block">{first}</span>
      <span className="block">
        {second} <span className="num font-semibold text-foreground">{value}</span>
      </span>
      <span className="block">
        <span data-count="" className="num font-semibold text-foreground">
          {count}
        </span>{' '}
        {count === 1 ? 'check' : 'checks'}
      </span>
    </div>
  )
}

// A line that grows with time from `from` to `to` (percent of the axis).
function Line({ top, kind, from, to }: { top: number; kind: 'unaware' | 'fixing' | 'fixed'; from: number; to: number }) {
  const look =
    kind === 'unaware'
      ? 'border-t-2 border-dashed border-muted-foreground'
      : kind === 'fixing'
        ? 'border-t-[3px] border-primary'
        : 'border-t-[3px] border-status-up'
  return (
    <span
      className={`absolute ${look}`}
      style={{ top: `${top}px`, left: `${from}%`, width: `max(0%, calc((min(var(--t), ${to}) - ${from}) * 1%))` }}
    />
  )
}

function Tick({ row, top, at, fired, down }: { row: 'us' | 'others'; top: string; at: number; fired: boolean; down: boolean }) {
  const look = !fired
    ? 'border-border bg-background'
    : down
      ? 'outage-pop border-status-down bg-status-down'
      : 'outage-pop border-status-up bg-status-up'
  return (
    <span
      data-tick={row}
      data-check={fired ? (down ? 'down' : 'up') : undefined}
      className={`absolute z-10 -ml-[5.5px] h-[11px] w-[11px] rounded-full border-[1.5px] left-[var(--x)] ${top} ${look}`}
      style={xy(`${at}%`)}
    />
  )
}

function Person({ who, accessory, aware, walking, className }: { who: 'us' | 'others'; accessory: Accessory; aware: boolean; walking: boolean; className: string }) {
  const Thing = accessory === 'fixing' || accessory === 'urgent' ? Laptop : TreePalm
  return (
    <span
      data-person={who}
      data-accessory={accessory}
      className={`absolute z-20 -ml-[14px] h-7 w-7 left-[calc(var(--t)*1%)] transition-colors sm:-ml-[17px] sm:h-[34px] sm:w-[34px] ${aware ? 'text-primary' : 'text-muted-foreground'} ${className}`}
    >
      <PersonStanding className={`h-full w-full ${walking ? 'outage-walk' : ''}`} strokeWidth={2} />
      <span key={accessory} className={`outage-acc absolute left-[26px] top-[2px] h-[22px] w-[22px] sm:left-[32px] sm:top-[3px] sm:h-7 sm:w-7 ${ACCESSORY_COLOR[accessory]}`}>
        <Thing className="h-full w-full" strokeWidth={2} />
      </span>
      {accessory === 'vacation-done' && (
        <span className="absolute -top-1.5 left-[17px] h-3.5 w-3.5 rounded-full border-2 border-background bg-status-up sm:left-[21px]" />
      )}
    </span>
  )
}

function Chip({ id, on, ok, className, style, children }: { id: string; on: boolean; ok?: boolean; className: string; style?: CSSProperties; children: ReactNode }) {
  return (
    <span
      data-chip={id}
      data-on={on ? 'true' : 'false'}
      className={`absolute z-30 flex items-center gap-1.5 whitespace-nowrap rounded-md border bg-card px-2 py-[3px] text-xs font-semibold transition-[opacity,translate] duration-300 ${
        ok ? 'border-status-up text-status-up' : 'border-border text-foreground'
      } ${on ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0'} ${className}`}
      style={style}
    >
      {children}
    </span>
  )
}

function Words({ id, on, x, className, children }: { id: string; on: boolean; x: string; className: string; children: ReactNode }) {
  return (
    <span
      data-text={id}
      data-on={on ? 'true' : 'false'}
      className={`absolute left-[var(--x)] whitespace-nowrap text-xs font-semibold transition-opacity duration-300 ${on ? 'opacity-100' : 'opacity-0'} ${className}`}
      style={xy(x)}
    >
      {children}
    </span>
  )
}
